import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { LMBuilderEvent, LMBuilderModel } from "@components/builder/services/lm-builder-model.service";
import { CdkDrag, CdkDragDrop, CdkDropList } from "@angular/cdk/drag-drop";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
  selector:    'lm-builder-column-totals',
  standalone: false,
  templateUrl: './lm-builder-column-totals.component.html',
  styleUrl: './lm-builder-column-totals.component.scss',
})
export class LMBuilderColumnTotalsComponent implements OnInit, OnDestroy{
  @Input() spec!: LMListPSpec;

  private readonly destroyer$ = new Subject<void>();
  protected readonly LMBuilderModel = LMBuilderModel;
  private totalModelMap = new Map<string, LMBuilderTotalFieldModel>();

  totalFields: Array<LMBuilderTotalFieldModel> = [];

  constructor(
    protected lmBuilderModel: LMBuilderModel
  ) {
    //no-op
  }

  ngOnInit(): void {
    this.lmBuilderModel.builderEvent$.pipe(
        takeUntil(this.destroyer$))
        .subscribe(event => {
          this.handleModelEvent(event);
        });
    this.initialize();
  }

  ngOnDestroy(): void {
    this.destroyer$.next();
    this.destroyer$.complete();
  }

  canDrop = (drag: CdkDrag, drop: CdkDropList): boolean => {
    if (drag.dropContainer.data.includes('lm-builder-field')) {
      return this.lmBuilderModel.allowColumnTotal(drag.data);
    } else if (drag.dropContainer.data.includes(LMBuilderModel.SUMMARY_TOTAL_LIST)) {
      return true;
    }
    return false;
  };

  drop(event: CdkDragDrop<string[]>) {
    if (event.previousContainer.data.includes('lm-builder-field')) {
      const builderField: LMBuilderField = event.item.data;
      if (this.lmBuilderModel.allowColumnTotal(builderField)) {
        this.lmBuilderModel.addColumnTotalDisplayField(-1, builderField);
      }
    }
  }

  private initialize() {
    this.totalModelMap.clear();

    if (this.spec.displayFields) {
      this.spec.displayFields.forEach(displayField => {
        if (displayField.type === 'field') {
          const specField = displayField.spec as LMFieldPSpec;
          if (specField.canTotal || specField.total !== 'NONE') {
            const fieldId = specField.fieldId;
            let model: LMBuilderTotalFieldModel;

            if (this.totalModelMap.has(fieldId)) {
              // @ts-ignore
              model = this.totalModelMap.get(fieldId);
              this.addDisplayFieldToModel(model, displayField);
            } else {
              model = this.buildTotalFieldModel(fieldId, displayField);
              this.totalModelMap.set(fieldId, model);
            }
          }
        }
      });

      this.buildTotalFields();
    }
  }

  private buildTotalFields() {
    const stagedTotalFields: Array<LMBuilderTotalFieldModel> = [];
    this.totalModelMap.forEach((model, fieldId) => {
      stagedTotalFields.push(model);
    });
    this.totalFields = stagedTotalFields;
  }

  private buildTotalFieldModel(fieldId:string, displayField:LMDisplayFieldPSpec): LMBuilderTotalFieldModel {
    const specField = displayField.spec as LMFieldPSpec;
    const total = specField.total? specField.total : 'NONE';

    const operatorModels:Array<LMBuilderTotalOperatorModel> = [];

    if (this.spec.implementsColumnarView) {
      operatorModels.push(this.buildTotalOperatorModel(fieldId, 'TOTAL', 'TOTAL' === total || 'NONE' === total ? displayField : undefined));
    } else {
      operatorModels.push(this.buildTotalOperatorModel(fieldId, 'TOTAL', 'TOTAL' === total || 'NONE' === total ? displayField : undefined));
      operatorModels.push(this.buildTotalOperatorModel(fieldId, 'RUNNING_TOTAL', 'RUNNING_TOTAL' === total ? displayField : undefined));
      operatorModels.push(this.buildTotalOperatorModel(fieldId, 'PERCENT_OF_TOTAL', 'PERCENT_OF_TOTAL' === total ? displayField : undefined));
    }

    return  {
      fieldId: fieldId,
      operatorModels: operatorModels,
    };
  }

  private buildTotalOperatorModel(fieldId:string, operator: string, displayField?: LMDisplayFieldPSpec): LMBuilderTotalOperatorModel {
    const specField: LMFieldPSpec | undefined = displayField?.type === 'field' ? displayField.spec as LMFieldPSpec : undefined;
    const total = specField ? specField.total : 'NONE';
    const checked = total == operator;
    let label = '';
    if (operator === 'TOTAL') {
      label = Soho.Locale.translate('totaling.option.label.total');
    } else if (operator === 'RUNNING_TOTAL') {
      label = Soho.Locale.translate('totaling.option.label.running');
    } else if (operator === 'PERCENT_OF_TOTAL') {
      label = Soho.Locale.translate('TOTALING_OPTION_LABEL_PERCENT1');
    } else {
      label = operator;
    }

    return  {
      operator:  operator,
      label: label,
      checked: checked,
      displayField: displayField,
      dai: `lm-builder-column-total-${fieldId}-${operator}`,
    };
  }

  private addDisplayFieldToModel(totalFieldModel: LMBuilderTotalFieldModel, displayField: LMDisplayFieldPSpec) {
    const specField = displayField.spec as LMFieldPSpec;
    const total = specField.total? specField.total : 'NONE';
    let operatorModel;

    switch (total) {
      // always replace, last in wins! shouldn't be more than one for a field!!
      case 'TOTAL':
      case 'RUNNING_TOTAL':
      case 'PERCENT_OF_TOTAL':
        operatorModel = this.findOperatorModel(total, totalFieldModel);
        if (operatorModel) {
          operatorModel.checked = true;
          operatorModel.displayField = displayField;
        }
        break;
      default:
    }
  }

  private findOperatorModel(operator: string, totalFieldModel: LMBuilderTotalFieldModel): LMBuilderTotalOperatorModel | undefined {
    return totalFieldModel.operatorModels.find(operatorModel => operatorModel.operator === operator);
  }

  private handleModelEvent(event:LMBuilderEvent) {
    if (event === LMBuilderEvent.DISPLAY_FIELD_ADDED
      || event === LMBuilderEvent.DISPLAY_FIELD_REMOVED
      || event === LMBuilderEvent.DISPLAY_FIELD_TOTAL_CHANGED) {
      this.initialize();
    }
  }

  updateTotalField(totalFieldModel: LMBuilderTotalFieldModel, operatorModel: LMBuilderTotalOperatorModel) {
    if (this.spec?.displayFields) {
      const fieldId = totalFieldModel.fieldId;

      if (operatorModel.operator === 'TOTAL') {
          this.updateTotalOperator(operatorModel);
      }

      if (operatorModel.checked) {
        this.addTotalOperator(fieldId, operatorModel);
      } else {
        this.removeTotalOperator(fieldId, operatorModel);
      }

      this.lmBuilderModel.builderEvent$.next(LMBuilderEvent.COLUMN_TOTALS_CHANGED);
    }
  }

  private updateTotalOperator(operatorModel: LMBuilderTotalOperatorModel) {
    const displayField = operatorModel.displayField;
    const specField = displayField ? displayField.spec as LMFieldPSpec : undefined;

    if (specField) {
      specField.total = operatorModel.checked ? 'TOTAL' : 'NONE';
    }
  }

  private addTotalOperator(fieldId: string, operatorModel: LMBuilderTotalOperatorModel) {
    if (this.spec?.displayFields) {
      switch (operatorModel.operator) {
        case 'RUNNING_TOTAL':
        case 'PERCENT_OF_TOTAL':

          const index = this.getDisplayFieldIndexForField(fieldId);
          if (index > -1) {
            const baseDisplayField = this.spec.displayFields[index];
            const totalDisplayField = JSON.parse(JSON.stringify(baseDisplayField));
            totalDisplayField.key = -1;
            totalDisplayField.label.type = 'explicit';
            totalDisplayField.label.messageRule = operatorModel.label;
            totalDisplayField.label.disaplay = operatorModel.label;
            const specField = totalDisplayField.spec as LMFieldPSpec;
            specField.total = operatorModel.operator;

            this.lmBuilderModel.insertField(index + 1, this.spec.displayFields, totalDisplayField);
          }

          break;
        default:
      }
    }
  }

  private removeTotalOperator(fieldId: string, operatorModel: LMBuilderTotalOperatorModel) {
    switch (operatorModel.operator) {
      case 'RUNNING_TOTAL':
      case 'PERCENT_OF_TOTAL':
        const index = this.getDisplayFieldIndexForTotalField(fieldId, operatorModel.operator);
        if (index > -1 ) {
          this.lmBuilderModel.removeDisplayField(index);
        }
        break;
      default:
    }
  }

  private getDisplayFieldIndexForTotalField(fieldId: string, total: 'TOTAL' | 'RUNNING_TOTAL' | 'PERCENT_OF_TOTAL' | 'NONE'): number {
    if (this.spec?.displayFields) {
      return this.spec.displayFields.findIndex(df => {
        if (df.type === "field") {
          const specField = df.spec as LMFieldPSpec;
          return specField?.fieldId === fieldId && specField?.total === total;
        }
      });
    }

    return -1;
  }

  private getDisplayFieldIndexForField(fieldId: string): number {
    if (this.spec?.displayFields) {
      return this.spec.displayFields.findIndex(df => {
        if (df.type === "field") {
          const specField = df.spec as LMFieldPSpec;
          return specField?.fieldId === fieldId;
        }
      });
    }

    return -1;
  }
}
